import { useEffect, useRef } from 'react';
import type { IRawLogs } from '@bingads-webui/common-types';

export interface PageActivityLogger {
  logTrace: (message: string, params?: IRawLogs) => void;
}

export const usePageActiveTimeTracker = (
  logger: PageActivityLogger,
  pageName: string,
  params?: IRawLogs
) => {
  const idleThreshold = 30000;
  const activeTimeRef = useRef(0);
  const lastActiveRef = useRef<number | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const startTracking = () => {
    if (!intervalRef.current) {
      lastActiveRef.current = Date.now();

      intervalRef.current = setInterval(() => {
        activeTimeRef.current += Date.now() - (lastActiveRef.current as number);
        lastActiveRef.current = Date.now();
      }, 1000); // Update every second
    }

    resetIdleTimer(); // Reset idle timer when user interacts. If user is idle for 30 seconds after this tracking is started, stop tracking.
  };

  const stopTracking = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const resetIdleTimer = () => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }

    idleTimerRef.current = setTimeout(() => {
      stopTracking();
      logger.logTrace(`${pageName} idle`);
    }, idleThreshold);
  };


  useEffect(() => {
    if (params) {
      logger.logTrace(`${pageName} page loaded`, params);
    } else {
      logger.logTrace(`${pageName} page loaded`);
    }

    // Start idle timer immediately (to detect users who enter and do nothing)
    resetIdleTimer();

    // Log time when user leaves
    const logExitTime = () => {
      stopTracking();
      logger.logTrace(`${pageName} page view duration: ${activeTimeRef.current}`);
    };

    // Track user activity (resume tracking on interaction)
    const handleUserActivity = () => startTracking();

    // Pause tracking when the tab is hidden
    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopTracking();
      } else {
        startTracking();
      }
    };
    const handleFocus = () => startTracking();
    const handleBlur = () => stopTracking(); // Stop when user switches focus

    // Listen for user activity
    window.addEventListener('mousemove', handleUserActivity);
    window.addEventListener('keydown', handleUserActivity);
    window.addEventListener('scroll', handleUserActivity);

    // Detect tab/window changes
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    // Detect page unload
    window.addEventListener('beforeunload', logExitTime);

    return () => {
      logExitTime(); // Log time spent if user navigates within SPA
      window.removeEventListener('mousemove', handleUserActivity);
      window.removeEventListener('keydown', handleUserActivity);
      window.removeEventListener('scroll', handleUserActivity);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('beforeunload', logExitTime);

      // Clear the idle timer when navigating away from the page
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
    };
  }, [logger, pageName]);
};
